"use client";
// palette-exempt: הכפתור הרדיאלי משתמש בפלטת תכלת מקומית עבור קונטרסט של שליטת־על צפה
// ===================================================
// GAM Command Center — vClick
// Global floating radial control. A circle split into 5: a center hub that
// toggles the settings window, and 4 sectors (top/right/bottom/left) that each
// fire a (configurable, Phase 2) action on a single click. The whole circle is
// dragged freely anywhere on screen; position persists to localStorage.
// Spec: docs/_live/memory/APP-vclick.md (approved 2026-06-15). Phase 1.
// ===================================================

import { useEffect, useRef, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { PinkasTable } from "@/components/pinkas/PinkasTable";
import { ChatSurface } from "@/components/chat/ChatSurface";
import { useChatNotifications } from "@/contexts/ChatNotificationsContext";
import { Z } from "@/lib/ui/layers";
import { EditorOverlayProvider } from "@gam/editor";

type Pos = { x: number; y: number };
const POS_KEY = "vclick:pos";
const SIZE_KEY = "vclick:size";
const TABS_KEY = "vclick:tabs";
const DRAG_THRESHOLD = 10;

const SECTORS = [
  { key: "top", label: "מחברת" },
  { key: "right", label: "ימין" },
  { key: "bottom", label: "צ׳אט" },
  { key: "left", label: "שמאל" },
] as const;

const WIRED_SECTOR_NOTE: Record<string, string> = {
  top: "פנקס",
  bottom: "צ׳אט פנימי",
};

const CHAT_W = 384;
const CHAT_H = 420;

const TABS = [
  { key: "sectors", label: "מקטעים" },
  { key: "appearance", label: "מראה" },
  { key: "position", label: "מיקום" },
] as const;

// donut-wedge path on a 100x100 viewBox (center 50,50), R outer, r inner
function wedge(a1: number, a2: number, R = 46, r = 20, C = 50): string {
  const pt = (ang: number, rad: number) => [
    C + rad * Math.cos((ang * Math.PI) / 180),
    C + rad * Math.sin((ang * Math.PI) / 180),
  ];
  const [ox, oy] = pt(a1, r);
  const [ax, ay] = pt(a1, R);
  const [bx, by] = pt(a2, R);
  const [ix, iy] = pt(a2, r);
  return `M${ox},${oy} L${ax},${ay} A${R},${R} 0 0 1 ${bx},${by} L${ix},${iy} A${r},${r} 0 0 0 ${ox},${oy} Z`;
}
// sectors centered on up/right/down/left, boundaries on the diagonals
const WEDGES: Record<string, string> = {
  right: wedge(-45, 45),
  bottom: wedge(45, 135),
  left: wedge(135, 225),
  top: wedge(225, 315),
};

function clampPos(p: Pos, size: number): Pos {
  const maxX = Math.max(8, window.innerWidth - size - 8);
  const maxY = Math.max(8, window.innerHeight - size - 8);
  return {
    x: Math.min(Math.max(8, p.x), maxX),
    y: Math.min(Math.max(8, p.y), maxY),
  };
}

export function VClick() {
  const [pos, setPos] = useState<Pos | null>(null);
  const [size, setSize] = useState(84);
  const [open, setOpen] = useState(false);
  const [pinkasOpen, setPinkasOpen] = useState(false);
  const [pinkasExpanded, setPinkasExpanded] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [tabsOn, setTabsOn] = useState<Record<string, boolean>>({
    sectors: true,
    appearance: true,
    position: true,
  });
  const draggingRef = useRef(false);
  const justDraggedRef = useRef(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);
  const { totalUnread } = useChatNotifications();

  useEffect(() => {
    if (!chatOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setChatOpen(false);
    };
    const onPointerDownOutside = (e: PointerEvent) => {
      const el = chatPanelRef.current;
      if (el && !el.contains(e.target as Node)) setChatOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDownOutside);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDownOutside);
    };
  }, [chatOpen]);

  // init from localStorage (client only — avoids SSR hydration mismatch)
  useEffect(() => {
    let p: Pos | null = null;
    try {
      const raw = localStorage.getItem(POS_KEY);
      if (raw) p = JSON.parse(raw);
    } catch {
      /* ignore malformed localStorage */
    }
    const sz = Number(localStorage.getItem(SIZE_KEY)) || 84;
    setSize(sz);
    if (!p) p = { x: window.innerWidth - sz - 24, y: window.innerHeight - sz - 24 };
    setPos(clampPos(p, sz));
    try {
      const t = JSON.parse(localStorage.getItem(TABS_KEY) || "null");
      if (t && typeof t === "object") setTabsOn(t);
    } catch {
      /* ignore malformed localStorage */
    }
  }, []);

  useEffect(() => {
    if (pos) localStorage.setItem(POS_KEY, JSON.stringify(pos));
  }, [pos]);
  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clampPos(p, size) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [size]);
  useEffect(() => {
    localStorage.setItem(SIZE_KEY, String(size));
  }, [size]);
  useEffect(() => {
    localStorage.setItem(TABS_KEY, JSON.stringify(tabsOn));
  }, [tabsOn]);

  function onPointerDown(e: React.PointerEvent) {
    const startX = e.clientX;
    const startY = e.clientY;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    draggingRef.current = false;
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!draggingRef.current && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
        draggingRef.current = true;
      }
      if (draggingRef.current) {
        const nx = Math.max(4, Math.min(window.innerWidth - size - 4, ev.clientX - offX));
        const ny = Math.max(4, Math.min(window.innerHeight - size - 4, ev.clientY - offY));
        setPos({ x: nx, y: ny });
      }
    };
    const up = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      if (draggingRef.current) {
        justDraggedRef.current = true;
        setTimeout(() => (justDraggedRef.current = false), 60);
      }
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  }

  function sectorClick(key: string, label: string) {
    if (justDraggedRef.current) return;
    if (key === "top") { setPinkasOpen(true); return; } // wired sector: פנקס
    if (key === "bottom") { setChatOpen(true); return; } // wired sector: צ׳אט פנימי
    // other sectors: configurable placeholders (mapping arrives in Phase 2).
    setFlash(`מקטע ${label} — יוגדר בהגדרות`);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 1400);
  }

  function centerClick() {
    if (justDraggedRef.current) return;
    setOpen((o) => !o);
  }

  if (!pos) return null;

  return (
    <>
      {/* The floating radial button */}
      <div
        dir="rtl"
        onPointerDown={onPointerDown}
        style={{ position: "fixed", left: pos.x, top: pos.y, width: size, height: size, zIndex: Z.devTool }}
        className="relative cursor-grab active:cursor-grabbing select-none touch-none drop-shadow-[0_6px_16px_rgba(0,0,0,0.5)]"
        title="vClick — גרור להזזה · קליק על מקטע · מרכז להגדרות"
      >
        <div className="absolute left-1/2 -translate-x-1/2 bottom-[-11px] pointer-events-none z-[1]">
          <div className="relative">
            <div className="relative left-1/2 -translate-x-1/2 -translate-y-2 w-10 h-10 bg-[var(--cc-accent-500)] rounded-2xl shadow-[0_12px_16px_rgba(6,182,212,0.35)] rotate-[-6deg]">
              <div className="absolute inset-1 rounded-xl bg-white/95 flex items-center justify-center">
                <MessageCircle className="size-4 text-[var(--cc-accent-500)]" strokeWidth={2.5} />
              </div>
            </div>
            <div className="absolute left-1/2 -translate-x-1/2 top-[23px] h-0 w-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent border-t-[var(--cc-accent-500)]" />
          </div>
        </div>
        <svg viewBox="0 0 100 100" width={size} height={size} style={{ overflow: "visible" }}>
          {SECTORS.map((s) => (
            <path
              key={s.key}
              d={WEDGES[s.key]}
              onClick={() => sectorClick(s.key, s.label)}
              className="fill-slate-700 hover:fill-teal-600 stroke-slate-900 transition-colors"
              strokeWidth={1.5}
            />
          ))}
          <circle
            cx={50}
            cy={50}
            r={17}
            onClick={centerClick}
            className={`stroke-slate-900 transition-colors ${open ? "fill-teal-400" : "fill-teal-500 hover:fill-teal-400"}`}
            strokeWidth={1.5}
          />
          <text x={50} y={53} textAnchor="middle" className="fill-white" style={{ fontSize: 10, fontWeight: 800, pointerEvents: "none" }}>
            v
          </text>
          {totalUnread > 0 && (
            <>
              <circle cx={70} cy={73} r={7} className="fill-[var(--cc-accent-500)] stroke-[var(--bg-chrome)]" strokeWidth={1} style={{ pointerEvents: "none" }} />
              <text x={70} y={76} textAnchor="middle" className="fill-[var(--bg-chrome)]" style={{ fontSize: 8, fontWeight: 800, pointerEvents: "none" }}>
                {totalUnread > 9 ? "9+" : totalUnread}
              </text>
            </>
          )}
        </svg>
      </div>

      {/* Transient sector feedback (Phase 1 placeholder) */}
      {flash && (
        <div
          style={{ position: "fixed", left: pos.x, top: pos.y - 30, zIndex: Z.devTool + 1 }}
          className="px-2 py-1 rounded-md bg-slate-900/90 border border-teal-500/40 text-teal-100 text-[11px] whitespace-nowrap pointer-events-none"
        >
          {flash}
        </div>
      )}

      {/* Settings window — tabbed side-menu pattern (mirrors the colors popover) */}
      {open && (
        <VClickSettings
          anchor={pos}
          size={size}
          onSize={setSize}
          tabsOn={tabsOn}
          onToggleTab={(k) => setTabsOn((t) => ({ ...t, [k]: !t[k] }))}
          onResetPos={() =>
            setPos({ x: window.innerWidth - size - 24, y: window.innerHeight - size - 24 })
          }
          onClose={() => setOpen(false)}
        />
      )}

      {/* פנקס — shared Gal<->Claude notebook (top sector) */}
      {pinkasOpen && (
        <div dir="rtl" style={{ zIndex: Z.devTool + 3 }} className="fixed inset-0 flex items-start justify-center pt-16">
          <div className="absolute inset-0 bg-[var(--theme-bg)] opacity-60" onClick={() => setPinkasOpen(false)} />
          {/* One workspace for panel and page; expand = near full screen, same state. */}
          <div
            className="relative flex flex-col overflow-hidden rounded-2xl border border-[var(--theme-border)] bg-[var(--theme-bg)] shadow-2xl"
            style={
              pinkasExpanded
                ? { width: "98vw", height: "calc(100vh - 5rem)" }
                : { resize: "both", width: "min(900px, 94vw)", height: "80vh", minWidth: 360, minHeight: 320, maxWidth: "96vw", maxHeight: "92vh" }
            }
          >
            <EditorOverlayProvider zIndex={Z.devTool + 4}>
              <PinkasTable
                variant="panel"
                onClose={() => setPinkasOpen(false)}
                onOpenFullPage={() => setPinkasOpen(false)}
                expanded={pinkasExpanded}
                onToggleExpand={() => setPinkasExpanded((v) => !v)}
              />
            </EditorOverlayProvider>
          </div>
        </div>
      )}

      {/* צ׳אט פנימי — bottom sector, anchored near the donut */}
      {chatOpen && (
        <div
          ref={chatPanelRef}
          dir="rtl"
          style={{
            position: "fixed",
            zIndex: Z.devTool + 3,
            left: Math.max(10, Math.min(pos.x + size / 2 - CHAT_W / 2, window.innerWidth - CHAT_W - 10)),
            top: Math.max(10, pos.y - CHAT_H - 12),
            width: CHAT_W,
            height: CHAT_H,
          }}
          className="overflow-hidden bg-[var(--bg-popover)] border border-[var(--cc-accent-500)]/40 rounded-2xl shadow-2xl flex flex-col"
        >
          <div className="flex items-center justify-between border-b border-[var(--border-hairline)] px-3 py-2 shrink-0">
            <span className="text-xs font-semibold text-[var(--cc-accent-300)]">צ׳אט פנימי</span>
            <button onClick={() => setChatOpen(false)} aria-label="סגור" className="rounded-md p-1 text-[var(--fg-faint)] transition hover:bg-[var(--bg-muted)] hover:text-[var(--fg-default)]">
              <X className="size-3.5" />
            </button>
          </div>
          <div className="flex-1 min-h-0">
            <ChatSurface mode="floating" />
          </div>
        </div>
      )}
    </>
  );
}

interface SettingsProps {
  anchor: Pos;
  size: number;
  onSize: (n: number) => void;
  tabsOn: Record<string, boolean>;
  onToggleTab: (key: string) => void;
  onResetPos: () => void;
  onClose: () => void;
}

function VClickSettings({ anchor, size, onSize, tabsOn, onToggleTab, onResetPos, onClose }: SettingsProps) {
  // place the panel near the button, clamped to the viewport
  const W = 320;
  const left = typeof window !== "undefined" ? Math.max(10, Math.min(anchor.x - W + size, window.innerWidth - W - 10)) : 10;
  const top = typeof window !== "undefined" ? Math.min(anchor.y, window.innerHeight - 360) : 80;

  return (
    <div
      dir="rtl"
      style={{ position: "fixed", left, top, width: W, zIndex: Z.devTool + 2 }}
      className="bg-slate-800 border border-teal-500/40 rounded-xl shadow-2xl p-3 text-white"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-teal-200">הגדרות vClick</span>
        <button onClick={onClose} className="w-6 h-6 rounded-md text-slate-400 hover:text-white hover:bg-slate-700">
          ✕
        </button>
      </div>
      <div className="flex gap-3 items-start">
        {/* side menu — on/off per tab */}
        <div className="flex flex-col gap-1.5 w-28 shrink-0">
          {TABS.map((t) => {
            const on = tabsOn[t.key];
            return (
              <button
                key={t.key}
                onClick={() => onToggleTab(t.key)}
                className={`flex items-center justify-between gap-1 px-2 py-1.5 rounded-md border text-xs transition-colors ${
                  on ? "border-teal-500 text-white bg-teal-500/10" : "border-slate-600 text-slate-400 hover:border-teal-500"
                }`}
              >
                <span>{t.label}</span>
                <span className={`w-7 h-4 rounded-full relative ${on ? "bg-teal-500" : "bg-slate-600"}`}>
                  <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white ${on ? "left-0.5" : "right-0.5"}`} />
                </span>
              </button>
            );
          })}
        </div>
        {/* body — only enabled tabs */}
        <div className="flex-1 min-w-0 space-y-3">
          {tabsOn.sectors && (
            <div>
              <div className="text-xs text-slate-400 mb-1">מקטעים (מיפוי פעולה — שלב 2)</div>
              <div className="space-y-1">
                {SECTORS.map((s) => (
                  <div key={s.key} className="flex items-center justify-between text-xs bg-slate-900/40 rounded px-2 py-1">
                    <span className="text-slate-300">{s.label}</span>
                    <span className={WIRED_SECTOR_NOTE[s.key] ? "text-[var(--cc-accent-300)]" : "text-slate-500"}>
                      {WIRED_SECTOR_NOTE[s.key] ?? "(יוגדר)"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tabsOn.appearance && (
            <div>
              <div className="flex items-center justify-between text-xs text-slate-300 mb-1">
                <span>גודל</span>
                <span className="text-teal-300" style={{ direction: "ltr" }}>{size}px</span>
              </div>
              <input
                type="range"
                min={60}
                max={120}
                value={size}
                onChange={(e) => onSize(Number(e.target.value))}
                className="w-full accent-teal-500 cursor-pointer"
              />
            </div>
          )}
          {tabsOn.position && (
            <div>
              <div className="text-xs text-slate-400 mb-1">מיקום</div>
              <button
                onClick={onResetPos}
                className="w-full px-2 py-1.5 rounded-md border border-slate-600 text-xs text-slate-300 hover:border-teal-500 hover:text-teal-200"
              >
                אפס מיקום (פינה)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
